import { useEffect, useState } from 'react';
import type { EntityType, LinkedCounts } from '../types';
import {
  bulkRestore,
  bulkSoftDelete,
  ENTITY_LABEL,
  isLinkedItemsError,
  restoreOne,
  softDeleteOne,
} from '../api/softDelete';
import { useUndo } from '../context/UndoContext';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Entity type for label + API routing. */
  entity: EntityType;
  /** IDs to hide. Pass a single id for the detail-page case. */
  ids: number[];
  /** Called after successful soft-delete (also after a forced retry). The list
   *  page should invalidate its query cache here. */
  onSuccess?: () => void;
}

function linkedSummary(linked: LinkedCounts): string {
  const parts: string[] = [];
  if (linked.inserate) parts.push(`${linked.inserate} Inserate`);
  if (linked.verfahren) parts.push(`${linked.verfahren} Verfahren`);
  if (linked.einheiten) parts.push(`${linked.einheiten} Objekte`);
  if (linked.haeuser) parts.push(`${linked.haeuser} Häuser`);
  if (linked.anfragen) parts.push(`${linked.anfragen} Anfragen`);
  return parts.join(', ');
}

export default function ConfirmDeleteModal({ open, onClose, entity, ids, onSuccess }: Props) {
  const { pushUndoable } = useUndo();
  const [step, setStep] = useState<1 | 2>(1);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warning, setWarning] = useState<{
    counts: LinkedCounts;
    blockedIds?: number[];
  } | null>(null);

  // Reset state every time the modal re-opens.
  useEffect(() => {
    if (open) {
      setStep(1);
      setReason('');
      setError(null);
      setWarning(null);
      setBusy(false);
    }
  }, [open]);

  if (!open) return null;

  const count = ids.length;
  const label = ENTITY_LABEL[entity];
  const noun = count === 1 ? label.singular : `${count} ${label.plural}`;

  const performDelete = async (force: boolean) => {
    setBusy(true);
    setError(null);
    try {
      if (ids.length === 1) {
        try {
          await softDeleteOne(entity, ids[0], { reason: reason || undefined, force });
        } catch (err) {
          const linked = isLinkedItemsError(err);
          if (linked && !force) {
            setWarning({ counts: linked.linked });
            return;
          }
          throw err;
        }
        pushUndoable({
          label: `${label.singular} ausgeblendet`,
          invokerKey: { type: 'softDelete', entity, ids: [ids[0]] },
          undo: async () => {
            await restoreOne(entity, ids[0]);
            onSuccess?.();
          },
        });
      } else {
        const result = await bulkSoftDelete(entity, ids, { reason: reason || undefined, force });
        if (Object.keys(result.blocked).length > 0 && !force) {
          // Aggregate linked counts across blocked ids for the warning modal.
          const total: LinkedCounts = {};
          for (const c of Object.values(result.blocked)) {
            total.inserate = (total.inserate ?? 0) + (c.inserate ?? 0);
            total.verfahren = (total.verfahren ?? 0) + (c.verfahren ?? 0);
            total.einheiten = (total.einheiten ?? 0) + (c.einheiten ?? 0);
            total.haeuser = (total.haeuser ?? 0) + (c.haeuser ?? 0);
            total.anfragen = (total.anfragen ?? 0) + (c.anfragen ?? 0);
          }
          setWarning({ counts: total, blockedIds: Object.keys(result.blocked).map(Number) });
          if (result.deleted > 0) {
            // Some items were hidden — register them as undoable.
            const successIds = ids.filter((id) => !result.blocked[id] && !result.skipped.includes(id));
            if (successIds.length > 0) {
              pushUndoable({
                label: `${successIds.length} ${label.plural} ausgeblendet`,
                invokerKey: { type: 'softDelete', entity, ids: successIds },
                undo: async () => {
                  await bulkRestore(entity, successIds);
                  onSuccess?.();
                },
              });
            }
            onSuccess?.();
          }
          return;
        }
        const successIds = ids.filter((id) => !result.skipped.includes(id));
        if (successIds.length > 0) {
          pushUndoable({
            label: `${successIds.length} ${label.plural} ausgeblendet`,
            invokerKey: { type: 'softDelete', entity, ids: successIds },
            undo: async () => {
              await bulkRestore(entity, successIds);
              onSuccess?.();
            },
          });
        }
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      const msg = (err as { message?: string }).message || 'Ausblenden fehlgeschlagen';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        {step === 1 && !warning && (
          <>
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                {noun} ausblenden?
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
              <p>
                {count === 1
                  ? `Möchten Sie dieses ${label.singular} wirklich ausblenden?`
                  : `Möchten Sie diese ${count} ${label.plural} wirklich ausblenden?`}
              </p>
              <p className="text-xs text-slate-500">
                Das Item wird nicht endgültig gelöscht. Du kannst es kurz danach mit
                Strg+Z rückgängig machen, später kann der Stadt-Admin es im Archiv
                wiederherstellen.
              </p>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                className="px-4 py-2 text-sm rounded bg-amber-600 text-white hover:bg-amber-700"
              >
                Weiter
              </button>
            </div>
          </>
        )}

        {step === 2 && !warning && (
          <>
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Endgültige Bestätigung
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
              <p>
                Bitte bestätige das Ausblenden von <strong>{noun}</strong>.
              </p>
              <label className="block">
                <span className="text-xs font-medium text-slate-600">Grund (optional)</span>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={3}
                  placeholder="z.B. Duplikat, Falsche Erfassung, …"
                  className="mt-1 block w-full rounded border border-slate-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </label>
              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setStep(1)}
                disabled={busy}
                className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                Zurück
              </button>
              <button
                type="button"
                onClick={() => performDelete(false)}
                disabled={busy}
                className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Wird ausgeblendet…' : 'Endgültig ausblenden'}
              </button>
            </div>
          </>
        )}

        {warning && (
          <>
            <div className="px-6 py-5 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-900">
                Verknüpfte Items gefunden
              </h2>
            </div>
            <div className="px-6 py-4 space-y-3 text-sm text-slate-700">
              <p>
                {warning.blockedIds && warning.blockedIds.length > 0
                  ? `${warning.blockedIds.length} der ausgewählten ${label.plural} haben verknüpfte Sub-Items:`
                  : 'Dieses Item hat verknüpfte Sub-Items:'}
              </p>
              <p className="font-medium text-slate-900">{linkedSummary(warning.counts)}</p>
              <p className="text-xs text-slate-500">
                Bei "Trotzdem ausblenden" wird nur das Parent ausgeblendet. Die verknüpften
                Sub-Items bleiben sichtbar und referenzieren weiterhin das ausgeblendete Item.
              </p>
              {error && (
                <p className="text-sm text-red-600">{error}</p>
              )}
            </div>
            <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={busy}
                className="px-4 py-2 text-sm rounded border border-slate-300 hover:bg-slate-50 disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                type="button"
                onClick={() => performDelete(true)}
                disabled={busy}
                className="px-4 py-2 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
              >
                {busy ? 'Wird ausgeblendet…' : 'Trotzdem ausblenden'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
